/**
 * test-triple — experimental three-ground-lane battlefield (M66).
 *
 * Not a campaign map: it probes the readability ceiling of the M65 route model
 * and the placement economy when three ground lanes run at once.
 *
 * Layout intent
 * - GROUND A is the longest lane: a west switchback with six hairpins.
 * - GROUND B is the shortest and most direct: a near-straight centre spine.
 * - GROUND C is mid-length with wide sweeping turns down the east.
 * - The lanes are deliberately not parallel. A and B share a narrow seam in the
 *   upper middle; B and C share one lower down; only a couple of anchors reach
 *   all three, which is what makes the centre premium instead of default.
 * - AIR A crosses every ground lane on its way from bottom-left to top-right.
 */

import type { MapDefinition, WaveDefinition } from '../model/types'
import {
  airRoute,
  emptyGrid,
  groundRoute,
  horiz,
  joinPaths,
  rowsFromGrid,
  setPath,
  vert
} from './gridPaint'

export const TEST_TRIPLE_WIDTH = 22
export const TEST_TRIPLE_HEIGHT = 30

/** West switchback — longest lane, most corners, slowest to leak. */
function laneA() {
  return joinPaths([
    vert(2, 0, 3),
    horiz(3, 2, 6),
    vert(6, 3, 7),
    horiz(7, 6, 2),
    vert(2, 7, 11),
    horiz(11, 2, 6),
    vert(6, 11, 16),
    horiz(16, 6, 3),
    vert(3, 16, 20),
    horiz(20, 3, 7),
    vert(7, 20, 25),
    horiz(25, 7, 4),
    vert(4, 25, 29)
  ])
}

/** Centre spine — shortest lane, two gentle offsets only. */
function laneB() {
  return joinPaths([
    vert(11, 0, 6),
    horiz(6, 11, 9),
    vert(9, 6, 14),
    horiz(14, 9, 12),
    vert(12, 14, 22),
    horiz(22, 12, 10),
    vert(10, 22, 29)
  ])
}

/** East sweep — mid-length lane with long straights between wide turns. */
function laneC() {
  return joinPaths([
    vert(19, 0, 5),
    horiz(5, 19, 15),
    vert(15, 5, 10),
    horiz(10, 15, 19),
    vert(19, 10, 17),
    horiz(17, 19, 15),
    vert(15, 17, 24),
    horiz(24, 15, 18),
    vert(18, 24, 29)
  ])
}

/** Air lane: bottom-left to top-right, crossing A, B and C in turn. */
function laneAir() {
  return [
    { x: 0, y: 29 },
    { x: 0, y: 24 },
    { x: 8, y: 24 },
    { x: 8, y: 18 },
    { x: 14, y: 18 },
    { x: 14, y: 11 },
    { x: 21, y: 11 },
    { x: 21, y: 0 }
  ]
}

/**
 * Deterministic lane-pressure waves: one wave to isolate each lane's character,
 * then a mixed wave that loads all four routes at once.
 */
export function testTripleWaves(): WaveDefinition[] {
  return [
    {
      // Dense trickle down the long west switchback.
      id: 'triple-w1-dense-a',
      completionReward: 45,
      groups: [
        { enemy: 'normal', count: 14, interval: 0.5, delay: 0.5, routeId: 'ground-a' }
      ]
    },
    {
      // Armour up the short centre spine — least time to react.
      id: 'triple-w2-armor-b',
      completionReward: 55,
      groups: [
        { enemy: 'guardian', count: 4, interval: 1.8, delay: 0.5, routeId: 'ground-b' },
        { enemy: 'tank', count: 3, interval: 2.0, delay: 3.0, routeId: 'ground-b' }
      ]
    },
    {
      // Sprinters down the east sweep.
      id: 'triple-w3-fast-c',
      completionReward: 60,
      groups: [{ enemy: 'fast', count: 12, interval: 0.55, delay: 0.5, routeId: 'ground-c' }]
    },
    {
      // Everything at once; `fast` and `normal` each appear on two lanes.
      id: 'triple-w4-mixed',
      completionReward: 90,
      groups: [
        { enemy: 'normal', count: 10, interval: 0.7, delay: 0.5, routeId: 'ground-a' },
        { enemy: 'guardian', count: 3, interval: 2.0, delay: 0.5, routeId: 'ground-b' },
        { enemy: 'fast', count: 6, interval: 0.6, delay: 0.5, routeId: 'ground-b' },
        { enemy: 'fast', count: 8, interval: 0.6, delay: 0.5, routeId: 'ground-c' },
        { enemy: 'normal', count: 6, interval: 0.8, delay: 2.0, routeId: 'ground-c' },
        { enemy: 'flyer', count: 6, interval: 1.0, delay: 1.5, routeId: 'air-a' }
      ]
    }
  ]
}

export function buildTestTripleMap(): MapDefinition {
  const width = TEST_TRIPLE_WIDTH
  const height = TEST_TRIPLE_HEIGHT
  const grid = emptyGrid(width, height)

  const a = laneA()
  const b = laneB()
  const c = laneC()
  setPath(grid, a)
  setPath(grid, b)
  setPath(grid, c)

  return {
    schemaVersion: 1,
    id: 'test-triple',
    name: 'Test Triple Lane',
    width,
    height,
    rows: rowsFromGrid(grid),
    // Lane A stays mirrored in the legacy fields for single-route readers.
    spawn: { ...a[0]! },
    exit: { ...a[a.length - 1]! },
    path: a.map((cell) => ({ ...cell })),
    startingGold: 750,
    startingLives: 20,
    waves: testTripleWaves(),
    routes: [
      groundRoute('ground-a', a),
      groundRoute('ground-b', b),
      groundRoute('ground-c', c),
      airRoute('air-a', laneAir())
    ]
  }
}

export const TEST_TRIPLE_MAP: MapDefinition = buildTestTripleMap()
